import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Heart, Users, Activity, Clock, CheckCircle, Star, Stethoscope, Video, HeartPulse, UserCheck } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const { toast } = useToast();

  const handleContactSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "We will get back to you as soon as possible.",
    });
    e.target.reset();
  };

  const services = [
    {
      icon: Users,
      title: 'Staffing Solutions',
      description: 'Comprehensive healthcare staffing services tailored to your needs.',
      link: '/services#staffing',
    },
    {
      icon: Stethoscope,
      title: 'Physicians & Advanced Practice',
      description: 'Connect with top physicians and advanced practice providers.',
      link: '/physicians-advanced',
    },
    {
      icon: Activity,
      title: 'Locum Tenens',
      description: 'Flexible locum tenens staffing solutions for healthcare facilities.',
      link: '/services#locum',
    },
    {
      icon: Video,
      title: 'Telehealth',
      description: 'Modern telehealth staffing for the digital healthcare era.',
      link: '/telehealth',
    },
    {
      icon: HeartPulse,
      title: 'Nurses',
      description: 'Skilled nursing professionals ready to provide exceptional care.',
      link: '/nursing',
    },
  ];

  const whyChooseUs = [
    'Innovative Culture',
    'High-Quality Patient Care',
    'Skilled Professionals',
    'Dedicated Staff',
    'Optimizing Delivery',
    'Customer Centricity',
    'Continuous Improvement',
    'Forward Thinking',
    'Passion',
    'Trust',
  ];

  const stats = [
    { number: '99+', label: 'Expert Personals' },
    { number: '37+', label: 'Years of Experience' },
    { number: '18k', label: 'Satisfy Customers' },
  ];

  const skills = [
    { name: 'Good communication', percentage: 90 },
    { name: 'Organizational and clerical', percentage: 80 },
    { name: 'Medical knowledge', percentage: 95 },
    { name: 'Customer service', percentage: 87 },
  ];

  return (
    <div className="home-page">
      <PageMeta
        title="Home"
        description="BluekraftMed - Leading provider of healthcare staffing solutions. Physicians, nurses, telehealth, and locum tenens staffing for facilities nationwide."
        path="/"
      />
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-r from-cyan-50 to-blue-50 py-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="hero-content">
              <p className="text-cyan-600 font-semibold mb-4 tracking-wide">WE ARE BLUEKRAFTMED</p>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                STAFFING & TALENT<br />
                <span className="text-cyan-600">SOLUTIONS</span>
              </h1>
              <p className="text-gray-600 text-lg mb-4 leading-relaxed">
                Everything you do revolves around people.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                We assist you in developing, acquiring, and onboarding personnel in order to provide
                comprehensive patient care in exciting ways.
              </p>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-6 text-lg">
                Get Started
              </Button>
            </div>
            <div className="hero-image">
              <img
                src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=800&h=600&fit=crop"
                alt="Healthcare Professional"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">BLUEKRAFT MEDICALS</h2>
          <p className="text-xl text-gray-600">Leading Provider of Healthcare staffing services</p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardHeader>
                  <div className="bg-cyan-100 w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <service.icon className="w-7 h-7 text-cyan-600" />
                  </div>
                  <CardTitle className="text-lg text-center">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4 text-center">{service.description}</CardDescription>
                  <Link to={service.link} className="text-cyan-600 hover:text-cyan-700 font-semibold block text-center">
                    More details →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Bluekraft */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="flex items-center mb-6">
                <Heart className="w-12 h-12 text-red-500 mr-4" />
                <div>
                  <p className="text-sm text-gray-500">Fast and professional</p>
                  <h3 className="text-3xl font-bold">Why choose Bluekraft?</h3>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 md:order-2">
              <img
                src="https://images.unsplash.com/photo-1638202993928-7267aad84c31?w=800&h=600&fit=crop"
                alt="Medical Professional"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Heart className="w-16 h-16 text-white mx-auto mb-4" />
            <h3 className="text-3xl font-bold mb-4">We take the time you need</h3>
            <p className="text-xl">Best clinic with over 15 years of experience</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <h4 className="text-2xl font-bold mb-4">Call Us Now: +1 (469) 466 2313</h4>
            <p className="text-lg">We are available 24/7 anywhere you want</p>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">Experienced doctors</h3>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            <img src="https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=400&h=300&fit=crop" alt="Doctor" className="w-full h-64 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300" />
            <img src="https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?w=400&h=300&fit=crop" alt="Doctor" className="w-full h-64 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300" />
            <img src="https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?w=400&h=300&fit=crop" alt="Nurse" className="w-full h-64 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300" />
            <img src="https://images.pexels.com/photos/40568/medical-appointment-doctor-healthcare-40568.jpeg?w=400&h=300&fit=crop" alt="Medical" className="w-full h-64 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300" />
            <img src="https://images.unsplash.com/photo-1621862912856-0909fb7f14b7?w=400&h=300&fit=crop" alt="Healthcare" className="w-full h-64 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300" />
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/35260717/pexels-photo-35260717.jpeg?w=800&h=600&fit=crop"
                alt="Healthcare Professional"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-8">About Medical</h3>
              {skills.map((skill, index) => (
                <div key={index} className="mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="font-semibold">{skill.name}</span>
                    <span className="text-cyan-600 font-bold">{skill.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-cyan-600 h-3 rounded-full transition-all duration-1000"
                      style={{ width: `${skill.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold mb-6">Experience and Reach</h3>
              <p className="text-xl text-gray-600 mb-4">The medical reach experience with knowledge</p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Physicians, scientists and other medical experts dedicate a portion of their clinical time to this site, we are in the unique position to give you access to the knowledge and experience of Medical Physicians, scientists and other medical experts dedicate a portion of their clinical time to this site.
              </p>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
                Read more details
              </Button>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=800&h=600&fit=crop"
                alt="Healthcare Experience"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Patient Services */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?w=800&h=600&fit=crop"
                alt="Patient Services"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="flex items-center mb-6">
                <Activity className="w-12 h-12 text-cyan-600 mr-4" />
                <h3 className="text-4xl font-bold">Patient Online Services</h3>
              </div>
              <p className="text-xl text-gray-600 mb-4">Manage all of your appointments</p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Physicians, scientists and other medical experts dedicate their clinical time to this site, we are in unique position to give you access to the knowledge and experience of Medical.
              </p>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
                Read more details
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-gradient-to-r from-gray-100 to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Mail className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
              <h3 className="text-4xl font-bold mb-4">Contact Us</h3>
              <p className="text-gray-600">We will get back as soon as possible.</p>
            </div>
            <form onSubmit={handleContactSubmit} className="bg-white p-8 rounded-lg shadow-xl">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Input placeholder="Full name" required />
                <Input type="tel" placeholder="Your phone" required />
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Input type="email" placeholder="Your email" required />
                <Input placeholder="Subject" required />
              </div>
              <Textarea placeholder="Description" rows={5} className="mb-4" required />
              <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-6 text-lg">
                Send request
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;